import React, { useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, AlertTriangle, FileText } from "lucide-react";

const STYLES = [
  { key: "generic", label: "Generic professional" },
  { key: "article9", label: "Article 9 (NY MHL)" },
  { key: "opwdd", label: "OPWDD" },
  { key: "omh", label: "OMH" },
];
const KINDS = [
  { key: "incident", label: "Incident report" },
  { key: "behavior", label: "Behavior note" },
  { key: "progress", label: "Progress note" },
];

export default function NoteAssistantPage() {
  const [rough, setRough] = useState("");
  const [kind, setKind] = useState("incident");
  const [style, setStyle] = useState("generic");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async () => {
    if (!rough.trim()) return toast.error("Type your rough notes first.");
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/note-rewrite", {
        rough_text: rough, note_kind: kind, style,
      });
      setResult(data.result || data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally {
      setLoading(false);
    }
  }, [rough, kind, style]);

  const copyOut = () => {
    if (!result?.rewritten) return;
    navigator.clipboard.writeText(result.rewritten);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-4xl">
      <div>
        <p className="text-small-caps">AI · Documentation</p>
        <h1 className="text-h1 mt-1">Note Assistant</h1>
        <p className="text-sm text-slate-500 mt-1">Convert rough notes into clean professional documentation. AI never invents facts; missing details are flagged.</p>
      </div>

      <div className="nv-card p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-small-caps">Note type</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="note-kind-select">
              {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-small-caps">Style</span>
            <select value={style} onChange={(e) => setStyle(e.target.value)} className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="note-style-select">
              {STYLES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="text-small-caps">Rough notes</span>
          <textarea
            value={rough} onChange={(e) => setRough(e.target.value)}
            rows={6}
            placeholder="Type quick rough notes — date/time, who was present, what happened, what staff did, outcome…"
            className="mt-1.5 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            data-testid="note-rough-input"
          />
        </label>
        <div className="flex justify-end">
          <button
            onClick={submit}
            disabled={loading || !rough.trim()}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-60"
            data-testid="note-rewrite-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loading ? "Rewriting…" : "Rewrite with AI"}
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-4" data-testid="note-result">
          <div className="nv-card p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <FileText size={16} className="text-indigo-600" />
                <span>Rewritten note</span>
                <span className="text-[11.5px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                  {KINDS.find((k) => k.key === kind)?.label} · {STYLES.find((s) => s.key === style)?.label}
                </span>
              </div>
              <button
                onClick={copyOut}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 px-3 py-1.5 rounded-md"
                data-testid="note-copy-btn"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-800 whitespace-pre-wrap">{result.rewritten}</p>
          </div>
          {Array.isArray(result.gaps) && result.gaps.length > 0 && (
            <div className="nv-card p-5 border-amber-200 bg-amber-50/50">
              <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                <AlertTriangle size={14} /> Missing details to fill in
              </div>
              <ul className="mt-2 list-disc ml-5 space-y-1 text-sm text-amber-900">
                {/* AI gap items are short strings unique per generation; index is stable for the lifetime of `result`. */}
                {result.gaps.map((g, i) => <li key={`gap-${i}`}>{g}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
